import * as React from "react";
import { useState } from "react";
import {
  Box, Typography,
  IconButton, Menu, MenuItem, Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CheckIcon from "@mui/icons-material/Check";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import {
  useGetFieldAdminVehiclesQuery,
  useSetFieldAdminVehicleStatusMutation,
  useGetFieldAdminTeamOverviewQuery,
} from "./fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { OK_GREEN_TEXT, WARN_YELLOW, teamLabel, formatDayTime } from "./designTokens";
import { useDispatch } from "react-redux";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { FAPageState } from "./FAStates";

import { FA_MENU_PAPER_SX, FAStatTile, FAStatusPill, FAEmptyState } from "./FALayout";

import { FAFilterChips, FAPageShell, FAPageHeader } from "./FALayout";
import { VehicleDialog, RetireConfirmDialog } from "./FleetVehicleDialog";

const STATUS_META = {
  active: { label: "Active", bg: "rgba(34,197,94,0.12)", color: OK_GREEN_TEXT },
  in_service: { label: "In service", bg: "rgba(251,191,36,0.12)", color: WARN_YELLOW },
  retired: { label: "Retired", bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" },
};
// "On register" (not "All") reproduces the design's own default — retired hidden — since
// defaulting to All would silently pad the register with decommissioned vans on load.
const FILTERS = ["On register", "Active", "In service", "Unassigned", "Retired"];
const GRID_COLUMNS = { xs: "1fr auto", sm: "minmax(0,1.7fr) 0.9fr 1fr 1.1fr 64px" };

// vehicles.html's .tbl-row rules under @media (max-width: 720px). The .col-team and
// .col-inspect cells are display:none on mobile, which
// removes them as grid items — without named areas the status chip auto-places into the
// actions slot and the buttons drop to a second row. The design keeps actions spanning
// both rows on the right, with status tucked under the vehicle.
const GRID_AREAS = { xs: '"main actions" "meta actions"', sm: "none" };

const isUnassigned = (vehicle) => vehicle.status !== "retired" && vehicle.assigned_team_id == null;

export default function Fleet() {
  const { data, isSuccess, isError, error, refetch } = useGetFieldAdminVehiclesQuery();
  const { data: overview } = useGetFieldAdminTeamOverviewQuery();
  const [activeFilter, setActiveFilter] = useState("On register");
  const [editingVehicle, setEditingVehicle] = useState(null); // null closed, {} add, vehicle edit
  const [retireTarget, setRetireTarget] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // { anchorEl, vehicle } or null

  if (!isSuccess) return <FAPageState isError={isError} error={error} onRetry={refetch} count={4} tiles={4} />;

  const vehicles = data.vehicles;
  const teamId = data.team_id;

  const activeCount = vehicles.filter((v) => v.status === "active").length;
  const inServiceCount = vehicles.filter((v) => v.status === "in_service").length;
  const retiredCount = vehicles.filter((v) => v.status === "retired").length;
  const unassignedCount = vehicles.filter(isUnassigned).length;

  const FILTER_PREDICATES = {
    "On register": (v) => v.status !== "retired",
    Active: (v) => v.status === "active",
    "In service": (v) => v.status === "in_service",
    Unassigned: isUnassigned,
    Retired: (v) => v.status === "retired",
  };
  const CHIP_COUNTS = {
    "On register": vehicles.length - retiredCount,
    Active: activeCount,
    "In service": inServiceCount,
    Unassigned: unassignedCount,
    Retired: retiredCount,
  };
  const filtered = vehicles.filter(FILTER_PREDICATES[activeFilter]);

  return (
    <>
      <FAPageShell>
        {/* Page head */}
        <FAPageHeader
          title="Fleet"
          sub={<>{overview?.company_name ? (
                <><Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>{overview.company_name}</Box> · vehicles you manage</>
              ) : "Vehicles you manage"}</>}
          action={<Box
            onClick={() => setEditingVehicle({})}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "7px", px: "14px", py: "9px",
              backgroundColor: "rgba(253,185,64,0.1)", border: "1px solid rgba(253,185,64,0.3)",
              borderRadius: "8px", color: AMBER, fontSize: "0.82rem", fontWeight: 600, cursor: "pointer",
              "&:hover": { backgroundColor: "rgba(253,185,64,0.18)" },
            }}
          >
            <AddIcon sx={{ fontSize: "1.05rem" }} /> Add vehicle
          </Box>}
        />

        {/* Stat tiles */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: "10px", mb: "18px" }}>
          <FAStatTile label="Active" value={activeCount} sub="On the road" />
          <FAStatTile label="In service" value={inServiceCount} sub="Off-road / maintenance" />
          <FAStatTile label="Unassigned" value={unassignedCount} sub="Spare pool" />
          <FAStatTile label="Retired" value={retiredCount} sub="Decommissioned" />
        </Box>

        {/* Filter chips */}
        <FAFilterChips filters={FILTERS} counts={CHIP_COUNTS} active={activeFilter} onChange={setActiveFilter} />

        {/* Section label */}
        <Box sx={{ py: "6px", mb: "4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Fleet register
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>
            {filtered.length} vehicle{filtered.length === 1 ? "" : "s"}
          </Typography>
        </Box>

        {/* Register table / empty states */}
        {filtered.length === 0 ? (
          // The design keeps the empty state inside the register card rather than as loose
          // centred text on the page ground, so the card outline never disappears.
          <FAEmptyState card py="70px">{vehicles.length === 0
                ? "No vehicles on the register yet — add a vehicle to get started."
                : "No vehicles match this filter."}</FAEmptyState>
        ) : (
          <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
            <Box
              sx={{
                display: { xs: "none", sm: "grid" }, gridTemplateColumns: GRID_COLUMNS, gap: "10px", px: "16px", py: "11px",
                fontSize: "0.64rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em",
                color: "rgba(255,255,255,0.45)", borderBottom: `1px solid ${BORDER}`, backgroundColor: "rgba(255,255,255,0.015)",
              }}
            >
              <span>Vehicle</span><span>Status</span><span>Assigned team</span><span>Last inspection</span><span />
            </Box>
            {filtered.map((vehicle, index) => (
              <VehicleRow
                key={vehicle.id}
                vehicle={vehicle}
                isLast={index === filtered.length - 1}
                onEdit={() => setEditingVehicle(vehicle)}
                onMenuOpen={(anchorEl) => setRowMenu({ anchorEl, vehicle })}
              />
            ))}
          </Box>
        )}
      </FAPageShell>

      <RowActionsMenu
        rowMenu={rowMenu}
        onClose={() => setRowMenu(null)}
        onEdit={setEditingVehicle}
        onRetire={setRetireTarget}
      />

      {editingVehicle ? (
        <VehicleDialog vehicle={editingVehicle} vehicles={vehicles} teamId={teamId} teamName={data.team_name} onClose={() => setEditingVehicle(null)} />
      ) : null}
      {retireTarget ? (
        <RetireConfirmDialog vehicle={retireTarget} onClose={() => setRetireTarget(null)} />
      ) : null}
    </>
  );
}

function VehicleRow({ vehicle, isLast, onEdit, onMenuOpen }) {
  const meta = STATUS_META[vehicle.status] || { label: vehicle.status, bg: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)" };
  // Per-row, not the FA's own team: the register lists vehicles assigned to other teams too.
  const assignedLabel = vehicle.assigned_team_id == null ? null : teamLabel(vehicle.assigned_team_name, vehicle.assigned_team_id);

  return (
    <Box
      onClick={onEdit}
      sx={{
        display: "grid", gridTemplateColumns: GRID_COLUMNS, gridTemplateAreas: GRID_AREAS, gap: { xs: "4px 10px", sm: "10px" }, alignItems: "center", px: "16px", py: "12px",
        fontSize: "0.86rem", borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.04)", cursor: "pointer",
        "&:hover": { backgroundColor: "rgba(253,185,64,0.04)" },
      }}
    >
      <Box sx={{ gridArea: { xs: "main", sm: "auto" }, display: "flex", alignItems: "center", gap: "11px", minWidth: 0 }}>
        <Box sx={{ width: "34px", height: "34px", borderRadius: "9px", backgroundColor: "rgba(253,185,64,0.12)", color: AMBER, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <LocalShippingOutlinedIcon sx={{ fontSize: "1.05rem" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {vehicle.make_model}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "1px", fontVariantNumeric: "tabular-nums" }}>
            {vehicle.registration}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ gridArea: { xs: "meta", sm: "auto" } }}>
        <FAStatusPill meta={meta} />
      </Box>

      <Box sx={{ display: { xs: "none", sm: "block" }, color: assignedLabel ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.4)", fontSize: "0.82rem" }}>
        {assignedLabel || "Unassigned"}
      </Box>

      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        {vehicle.last_inspection_at ? (
          <>
            <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
              {/* vehicles.html seeds this cell as "06:48 today" — the suffix was missing
                  here while Team's card had it, so one inspection read two ways. */}
              {formatDayTime(vehicle.last_inspection_at, { todaySuffix: true })}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)" }}>
              {vehicle.last_inspection_items_checked}/{vehicle.last_inspection_items_total} checked
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.82rem" }}>—</Typography>
        )}
      </Box>

      <Box sx={{ gridArea: { xs: "actions", sm: "auto" }, display: "flex", alignItems: "center", gap: "2px", justifyContent: "flex-end" }}>
        <IconButton
          size="small"
          aria-label="Edit vehicle"
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: AMBER, backgroundColor: "rgba(255,255,255,0.05)" } }}
        >
          <EditOutlinedIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
        <IconButton
          size="small"
          aria-label="More actions"
          onClick={(e) => { e.stopPropagation(); onMenuOpen(e.currentTarget); }}
          sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: AMBER, backgroundColor: "rgba(255,255,255,0.05)" } }}
        >
          <MoreVertIcon sx={{ fontSize: "1.05rem" }} />
        </IconButton>
      </Box>
    </Box>
  );
}

function RowActionsMenu({ rowMenu, onClose, onEdit, onRetire }) {
  const dispatch = useDispatch();
  const [setStatus] = useSetFieldAdminVehicleStatusMutation();
  const vehicle = rowMenu?.vehicle;

  // vehicles.html toasts a different string per destination, not one generic "saved".
  const TRANSITION_TOAST = {
    in_service: "Marked in-service",
    active: vehicle && vehicle.status === "retired" ? "Vehicle restored" : "Returned to active",
  };

  const handleTransition = async (status) => {
    onClose();
    if (!vehicle) return;
    const result = await setStatus({ vehicle_id: vehicle.id, status });
    if (!result.error) dispatch(showSuccess(TRANSITION_TOAST[status] || "Vehicle updated"));
  };

  return (
    <Menu
      anchorEl={rowMenu?.anchorEl}
      open={Boolean(rowMenu)}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: FA_MENU_PAPER_SX }}
    >
      <MenuItem onClick={() => { onClose(); if (vehicle) onEdit(vehicle); }}>
        <EditOutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Edit vehicle
      </MenuItem>
      {vehicle?.status === "active" ? (
        <MenuItem onClick={() => handleTransition("in_service")}>
          <BuildOutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Mark in-service
        </MenuItem>
      ) : vehicle?.status === "in_service" ? (
        <MenuItem onClick={() => handleTransition("active")}>
          <CheckIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Return to active
        </MenuItem>
      ) : null}
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      {vehicle?.status === "retired" ? (
        <MenuItem onClick={() => handleTransition("active")}>
          <CheckIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Restore vehicle
        </MenuItem>
      ) : (
        <MenuItem onClick={() => { onClose(); if (vehicle) onRetire(vehicle); }} data-danger="true">
          <ArchiveOutlinedIcon sx={{ fontSize: "1rem", mr: "10px" }} /> Retire vehicle
        </MenuItem>
      )}
    </Menu>
  );
}

